import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { playSupportChime, primeChimeOnGesture } from '../lib/supportChime';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 45 * 1000; // 45s — snappy support-reply detection

/**
 * Messages icon for the top header (Feb 2026 — Student Dashboard spec).
 *
 * - For Students/Parents/Teachers: routes to /support/my-requests.
 * - For Super_Admin / Academic_Supervisor: routes to /support/inbox.
 * - Shows the unread badge (capped at "9+") from
 *   GET /api/support/conversations/unread-count.
 * - Hides itself when the user isn't authenticated yet.
 */
export const MessagesBell = ({ silent = false } = {}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [unread, setUnread] = useState(0);
  const [ringing, setRinging] = useState(false);
  const prevUnread = useRef(null);
  const authDeadRef = useRef(false);

  useEffect(() => {
    primeChimeOnGesture();
    const isInitiator = user && ['Student', 'Parent', 'Teacher'].includes(user.role);
    const fetchCount = async () => {
      if (!user) return;
      try {
        const r = await axios.get(`${API_URL}/api/support/conversations/unread-count`, { withCredentials: true });
        const next = r.data?.unread || 0;
        // Jul 2026 — distinctive chime + bell wiggle when a NEW support
        // reply lands for a Student/Parent/Teacher (count increased
        // since the previous poll; first poll never chimes).
        if (isInitiator && prevUnread.current !== null && next > prevUnread.current) {
          playSupportChime();
          setRinging(true);
          setTimeout(() => setRinging(false), 3000);
        }
        prevUnread.current = next;
        setUnread(next);
      } catch (err) {
        // Aug 2026 prod-health — stop polling after session expiry (401s
        // from stale overnight tabs). Re-login remounts and restarts.
        if (err?.response?.status === 401) authDeadRef.current = true;
      }
    };
    fetchCount();
    const id = setInterval(() => {
      if (!authDeadRef.current && !document.hidden) fetchCount();
    }, POLL_MS);
    const onVis = () => { if (!document.hidden && !authDeadRef.current) fetchCount(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [user]);

  if (!user) return null;
  // Feb 2026 — header cleanup: bell icon removed from top bar, but the
  // polling effect above still runs so the support chime + unread count
  // keep working. When rendered in `silent` mode the component returns
  // nothing while the interval stays alive.
  if (silent) return null;

  const isAdmin = user.role === 'Super_Admin' || user.role === 'Academic_Supervisor';
  const to = isAdmin ? '/support/inbox' : '/support/my-requests';
  const label = isAdmin
    ? t('Support Inbox', 'صندوق الدعم')
    : t('My messages', 'رسائلي');

  return (
    <Link
      to={to}
      className="relative p-2 text-[#5C6B8C] hover:bg-[#F5F5F0] rounded-sm transition-colors inline-flex"
      data-testid="header-messages-btn"
      title={label}
      aria-label={label}
    >
      <MessageSquare size={20} className={ringing ? 'mesp-bell-ring' : ''} />
      {unread > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
          data-testid="messages-bell-badge"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </Link>
  );
};

export default MessagesBell;
